import gql from "graphql-tag";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {Errors} from "@khanacademy/wonder-stuff-core";
import {useMutation, useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {getPromptVariable, setPromptVariable} from "@khan/guide-core-helpers";
import {KAError} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";

import {useGuideLanguages} from "../queries/use-guide-languages-query.ts";

import type {
    GuidePreferencesType,
    SetGuidePreferencesType,
} from "./__graphql-types__/index.ts";
import type {SetGuidePreferencesVariables} from "./__graphql-types__/SetGuidePreferences.ts";
import {
    DEFAULT_GUIDE_PREFERENCES,
    GuidePreferencesContext,
    type GuidePreferences,
    type GuidePreferencesContextState,
} from "./guide-preferences-context.ts";

// eslint-disable-next-line react-refresh/only-export-components -- porting over from old code.
export const AI_GUIDE_PREFERENCES_QUERY: GqlOperation<
    GuidePreferencesType["response"],
    GuidePreferencesType["variables"]
> = gqlOp(gql`
    query GuidePreferences {
        user {
            id
            aiGuidePreferences {
                readingLevel
                guideLanguageCode
                guideVoiceDescriptor
                showDABs
            }
        }
    }
`);

// eslint-disable-next-line react-refresh/only-export-components -- porting over from old code.
export const AI_GUIDE_PREFERENCES_MUTATION: GqlOperation<
    SetGuidePreferencesType["response"],
    SetGuidePreferencesType["variables"]
> = gqlOp(gql`
    mutation SetGuidePreferences(
        $readingLevel: AIGuideReadingLevel
        $guideLanguageCode: String
        $guideVoiceDescriptor: String
        $showDABs: Boolean
    ) {
        setPreferencesForUser(
            readingLevel: $readingLevel
            guideLanguageCode: $guideLanguageCode
            guideVoiceDescriptor: $guideVoiceDescriptor
            showDABs: $showDABs
        ) {
            error {
                code
            }
        }
    }
`);

interface Props {
    /**
     * Children to render inside the provider.
     */
    children: React.ReactNode;
}

export const GuidePreferencesProvider = (props: Props): React.ReactElement => {
    const {children} = props;
    const [mutate] = useMutation(AI_GUIDE_PREFERENCES_MUTATION);
    const [guidePreferencesResult, refetch] = useQuery(
        AI_GUIDE_PREFERENCES_QUERY,
        {
            fetchPolicy: FetchPolicy.NetworkOnly,
            skip: !PermissionsInfo.isLoggedIn,
        },
    );
    const guideLanguagesResult = useGuideLanguages();
    const [isLoading, setIsLoading] = useState(false);
    const [preferences, setPreferences] = useState<GuidePreferences>(
        DEFAULT_GUIDE_PREFERENCES,
    );

    const handleSetPreferences = useCallback(
        async (variables: SetGuidePreferencesVariables): Promise<void> => {
            try {
                setIsLoading(true);
                const result = await mutate(variables);

                if (result.status === "error") {
                    throw new KAError(
                        "Failed to set AI Guide user preferences",
                        Errors.Internal,
                        {cause: result.error},
                    );
                }

                if (
                    result.status === "success" &&
                    result.data.setPreferencesForUser.error
                ) {
                    throw new KAError(
                        "Failed to set AI Guide user preferences",
                        Errors.Internal,
                        {cause: result.data.setPreferencesForUser.error.code},
                    );
                }
                // Refetch the preferences to make sure we have the latest
                // if there are no errors.
                refetch();
            } catch (e: any) {
                if (e instanceof KAError) {
                    throw e;
                }

                // Otherwise throw a generic error
                throw new KAError(
                    "Failed to set AI Guide user preferences",
                    Errors.Internal,
                );
            } finally {
                setIsLoading(false);
            }
        },
        [mutate, refetch],
    );

    // Sync the preferences state with the result of the query.
    useEffect(() => {
        setIsLoading(
            guidePreferencesResult.status === "loading" ||
                guideLanguagesResult.status === "loading",
        );

        if (
            guidePreferencesResult.status !== "success" ||
            guideLanguagesResult.status !== "success"
        ) {
            return;
        }

        const aiGuidePreferences =
            guidePreferencesResult.data.user?.aiGuidePreferences;
        const defaultLanguageCode =
            guideLanguagesResult.data.defaultLanguageCode;

        if (aiGuidePreferences) {
            const preferences: GuidePreferences = {
                // sync-start:default-reading-level 2079011510 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/ai_guide_preferences.go
                readingLevel: aiGuidePreferences.readingLevel ?? "LOW",
                // sync-end:default-reading-level
                guideLanguageCode:
                    aiGuidePreferences.guideLanguageCode ?? defaultLanguageCode,
                guideVoiceDescriptor:
                    aiGuidePreferences.guideVoiceDescriptor ?? null,
                showDABs: aiGuidePreferences.showDABs ?? null,
            };
            setPreferences(preferences);

            // Keep prompt variables in sync with the preferences.
            if (
                getPromptVariable("ReadingLevel") !== preferences.readingLevel
            ) {
                setPromptVariable("ReadingLevel", preferences.readingLevel);
            }

            if (
                getPromptVariable("GuideLanguage") !==
                preferences.guideLanguageCode
            ) {
                const guideLanguage =
                    guideLanguagesResult.data.guideLanguages.find(
                        (lang) => lang.id === preferences.guideLanguageCode,
                    )?.displayName;

                setPromptVariable("GuideLanguage", guideLanguage);
            }
        }
    }, [guidePreferencesResult, guideLanguagesResult]);

    const context: GuidePreferencesContextState = useMemo(
        () => ({
            isLoading,
            preferences,
            refetchPreferences: refetch,
            setPreferences: handleSetPreferences,
        }),
        [handleSetPreferences, preferences, refetch, isLoading],
    );

    return (
        <GuidePreferencesContext.Provider value={context}>
            {children}
        </GuidePreferencesContext.Provider>
    );
};
